/**
 * shared/ship_engineering.ts — CLIENT MIRROR of server/src/lib/ship_engineering.ts (space campaign slice G, 2026-09-30;
 * the server copy is authoritative — keep them byte-identical below the headers; verify:ship-engineering compares them).
 * ENGINEERING CREW AND SUBSYSTEMS: the two damageable groups, the §18.7 damage rule, the §18.3 effects, the 8-second
 * field patch, the NPC aim, judged vs reported damage, the crew leases and their epochs, the fit revision, the droid's
 * field stock and the HUD's words. Pure.
 */
import { workCredit, beatTooSoon } from './space_survey';

// ── THE ROLLOUT ──────────────────────────────────────────────────────────────────────────────────────────────────
/** ABSENT = ON (`flagOn(ctx, KEY, true)`); false restores today's ship: no groups, the open turret, the droid unbounded. */
export const ENGINEERING_FLAG = 'crew_engineering_v1';
export const ENGINEERING_RULES_VERSION = 1;

// ── THE GROUPS (§18.3) ───────────────────────────────────────────────────────────────────────────────────────────
export type SubsystemGroup = 'engine' | 'weapons';
export const SUBSYSTEM_GROUPS: readonly SubsystemGroup[] = ['engine', 'weapons'];
/** What an attacker may select (T cycles hull → engines → weapons). `sensors` is named and not damageable yet. */
export type AimGroup = 'hull' | SubsystemGroup | 'sensors';
export const AIM_GROUPS: readonly AimGroup[] = ['hull', 'engine', 'weapons', 'sensors'];
export const GROUP_MAX = 100;
/** Healthy above, degraded from 1 through it, disabled at 0. One table — no hysteresis in the first release. */
export const DEGRADED_AT = 60;
export type GroupState = 'healthy' | 'degraded' | 'disabled';
export interface GroupHealth { engine: number; weapons: number }
export const FULL_HEALTH: Readonly<GroupHealth> = { engine: GROUP_MAX, weapons: GROUP_MAX };
export const GROUP_LABEL: Readonly<Record<SubsystemGroup, string>> = { engine: 'engine', weapons: 'weapons' };

export function isSubsystemGroup(g: string): g is SubsystemGroup { return g === 'engine' || g === 'weapons'; }
export function clampHealth(h: number): number {
  return Number.isFinite(h) ? Math.max(0, Math.min(GROUP_MAX, Math.round(h))) : GROUP_MAX;
}
export function groupState(h: number): GroupState {
  const v = clampHealth(h);
  return v > DEGRADED_AT ? 'healthy' : v > 0 ? 'degraded' : 'disabled';
}

// ── THE DAMAGE RULE (§18.7): after the shield arc, 30 % of what is left to a selected valid group ──────────────────
export const GROUP_SHARE = 0.3;
/** One report carries at most this much post-shield damage (a capital volley is 9 a bolt, a missile 22, a PvP blow
 *  ≤ 12 — a burst of them fits; more is not one blow). */
export const REPORT_DMG_MAX = 60;
export interface Allocation {
  /** What the hull takes (the client's own pool — gap 30). */
  hull: number;
  /** The group the selection reached, or null (all to hull). */
  group: SubsystemGroup | null;
  /** floor(floor(postShield) × 0.30) — the group's share before the cap. */
  groupDamage: number;
  /** What the group's health actually lost (capped at its remaining health). */
  applied: number;
  /** Overkill on the group — DISCARDED, never counted a second time against the hull. */
  discarded: number;
  reason: string;
}
export function allocateDamage(postShield: number, selected: string, health: Readonly<GroupHealth>): Allocation {
  const raw = Number.isFinite(postShield) ? Math.max(0, postShield) : 0;
  const whole = Math.floor(raw);
  const toHull = (reason: string): Allocation => ({ hull: raw, group: null, groupDamage: 0, applied: 0, discarded: 0, reason });
  if (selected === 'hull') return toHull('hull targeted');
  if (selected === 'sensors') return toHull('sensors are not a damageable group yet — the hull takes it');
  if (!isSubsystemGroup(selected)) return toHull('no such group — the hull takes it');
  const h = clampHealth(health[selected]);
  if (h <= 0) return toHull(`the ${GROUP_LABEL[selected]} is already disabled — the hull takes it`);
  const groupDamage = Math.floor(whole * GROUP_SHARE);
  const applied = Math.min(groupDamage, h);
  return {
    hull: raw - groupDamage, group: selected, groupDamage, applied, discarded: groupDamage - applied,
    reason: groupDamage === 0 ? `too light to reach the ${GROUP_LABEL[selected]}` : `${applied} to the ${GROUP_LABEL[selected]}`,
  };
}

// ── THE EFFECTS (§18.3) ──────────────────────────────────────────────────────────────────────────────────────────
export const ENGINE_DEGRADED_ACCEL = 0.7;
/** A disabled engine limps at 15 % of the hull's top speed; steering is retained; no boost, no overcruise. */
export const ENGINE_LIMP = 0.15;
export const WEAPONS_DEGRADED_CD = 1.3;
export interface FlightEffect { accel: number; limp: number | null; boost: boolean; cruise: boolean }
export function engineEffect(h: number): FlightEffect {
  const s = groupState(h);
  if (s === 'healthy') return { accel: 1, limp: null, boost: true, cruise: true };
  if (s === 'degraded') return { accel: ENGINE_DEGRADED_ACCEL, limp: null, boost: true, cruise: true };
  return { accel: ENGINE_DEGRADED_ACCEL, limp: ENGINE_LIMP, boost: false, cruise: false };
}
/** The cannons (the twin bolts) are the weapons group's mount; the missile rack is a separate mount and keeps firing. */
export interface GunEffect { online: boolean; cdMult: number }
export function weaponsEffect(h: number): GunEffect {
  const s = groupState(h);
  if (s === 'healthy') return { online: true, cdMult: 1 };
  if (s === 'degraded') return { online: true, cdMult: WEAPONS_DEGRADED_CD };
  return { online: false, cdMult: WEAPONS_DEGRADED_CD };
}

// ── THE FIELD PATCH (§18.3, §18.7): one kit, 8 exposed seconds, ≤ 20 up to 60; a hull application ≤ 10 HP ─────────
export const KIT_KEY = 'repair_kit';
export const FIELD_PATCH_MS = 8_000;
export const FIELD_PATCH_ADD = 20;
export const FIELD_CAP = 60;
export const HULL_PATCH_MAX = 10;
/** A running job with no beat for this long lapses: the sweep releases its kit. */
export const PATCH_STALE_S = 30;
/** The actor stays within this of the hull it patches (an engineer rides it; a solo pilot IS it). */
export const PATCH_RANGE_M = 3_000;
export type PatchTarget = SubsystemGroup | 'hull';
export function isPatchTarget(t: string): t is PatchTarget { return t === 'hull' || isSubsystemGroup(t); }
/** Why a patch of `target` cannot start (null = it can). The hull is the client's pool — never refused here. */
export function patchRefusal(target: string, health: Readonly<GroupHealth>): string | null {
  if (!isPatchTarget(target)) return 'Patch the engine, the weapons or the hull';
  if (target === 'hull') return null;
  const h = clampHealth(health[target]);
  if (h >= FIELD_CAP) return `The ${GROUP_LABEL[target]} reads ${h} — a field patch only reaches ${FIELD_CAP}; a dock restores the rest`;
  return null;
}
/** What one completed patch adds. A group: ≤ 20, never past 60 (0 = nothing left to do — release the kit). The hull:
 *  the receipt's bound (the client applies min(10, its missing HP) to its own pool). */
export function patchOutcome(target: PatchTarget, health: Readonly<GroupHealth>): { add: number; after: number } {
  if (target === 'hull') return { add: HULL_PATCH_MAX, after: 0 };
  const h = clampHealth(health[target]);
  const add = Math.max(0, Math.min(FIELD_PATCH_ADD, FIELD_CAP - h));
  return { add, after: h + add };
}
/** One beat's credit (F's metered-work rule): 0 for the first beat or after a > 3 s gap; never more than 2 s. */
export function patchCredit(lastBeatMs: number, nowMs: number): number { return workCredit(lastBeatMs, nowMs); }
export function patchBeatTooSoon(lastBeatMs: number, nowMs: number): boolean { return beatTooSoon(lastBeatMs, nowMs); }

// ── THE NPC AIM: what an NPC attacker selected, derived from its key (the server computes it; the client predicts it) ─
export type AttackerKind = 'pirate' | 'war' | 'patrol' | 'hunter' | 'player' | 'unknown';
export function isAttackerKind(k: string): k is AttackerKind {
  return k === 'pirate' || k === 'war' || k === 'patrol' || k === 'hunter' || k === 'player' || k === 'unknown';
}
function fnv32(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
  return h >>> 0;
}
export function npcAimGroup(kind: string, ref: string): AimGroup {
  if (kind === 'hunter') return 'engine';            // a bounty hunter cripples your drive — then takes its time
  if (ref.startsWith('raid')) return 'engine';       // a lane ambush wants the cargo, not the kill
  if (kind === 'war') return 'hull';                 // the lines trade broadsides
  const h = fnv32(ref) % 10;
  if (kind === 'patrol') return h < 3 ? 'weapons' : 'hull';
  if (kind === 'pirate') return h < 3 ? 'engine' : h < 5 ? 'weapons' : 'hull';
  return 'hull';
}

// ── PROVENANCE: judged (the server saw the blow) vs reported (the victim's client says so) ─────────────────────────
export type Provenance = 'judged' | 'reported';
export function provenanceOf(kind: string, judgedHit: boolean): Provenance { return kind === 'player' && judgedHit ? 'judged' : 'reported'; }
/** §18.7 last ¶: repair work may be subsidised ONLY for damage an authorised opposing source dealt and the server
 *  judged. A reported hit, a self-inflicted one, a friendly one never generates subsidised work. */
export function subsidyEligible(prov: Provenance, friendly: boolean): boolean { return prov === 'judged' && !friendly; }
/** The judged deficit after a blow: never more than the health actually missing. */
export function judgedAfterDamage(judged: number, applied: number, prov: Provenance, healthAfter: number): number {
  const missing = GROUP_MAX - clampHealth(healthAfter);
  const next = prov === 'judged' ? judged + Math.max(0, applied) : judged;
  return Math.max(0, Math.min(missing, next));
}
/** A repair restores the judged deficit first (what an operation may credit) — returns [eligibleRestored, judgedAfter]. */
export function judgedAfterRepair(judged: number, add: number, healthAfter: number): [number, number] {
  const eligible = Math.max(0, Math.min(judged, add));
  const missing = GROUP_MAX - clampHealth(healthAfter);
  return [eligible, Math.max(0, Math.min(missing, judged - eligible))];
}

// ── THE REPORT BUCKET: a victim's damage reports, rate-bounded per hull ─────────────────────────────────────────────
export const REPORT_BUCKET_MAX = 30;
export const REPORT_BUCKET_RATE = 15; // a second
export function reportBucket(tokens: number, lastMs: number, nowMs: number): { tokens: number; ok: boolean } {
  const dt = lastMs > 0 ? Math.max(0, (nowMs - lastMs) / 1000) : REPORT_BUCKET_MAX / REPORT_BUCKET_RATE; // a step back is no debt
  const t = Math.min(REPORT_BUCKET_MAX, (Number.isFinite(tokens) ? tokens : REPORT_BUCKET_MAX) + dt * REPORT_BUCKET_RATE);
  return t >= 1 ? { tokens: t - 1, ok: true } : { tokens: t, ok: false };
}

// ── THE CREW LEASES (§18.4): player, hull, hull life, role, granted actions, epoch, expiry, budget ─────────────────
export type CrewRole = 'gunner' | 'engineer';
export const CREW_ROLES: readonly CrewRole[] = ['gunner', 'engineer'];
export function isCrewRole(r: string): r is CrewRole { return r === 'gunner' || r === 'engineer'; }
/** What each station may do. A gunner aims / fires its mount and marks a contact; an engineer inspects, routes power
 *  within the preset envelope and applies reserved repair. Neither changes the fit, flies, jumps, jettisons, sells or
 *  spends the captain's money — a kit budget is the only spend an engineer carries. */
export const ROLE_ACTIONS: Readonly<Record<CrewRole, readonly string[]>> = {
  gunner: ['fire', 'mark'],
  engineer: ['inspect', 'route', 'repair'],
};
/** The seat a role takes in `space_crew` (the turret is the shipped seat; the engineer's station is new). */
export const ROLE_SEAT: Readonly<Record<CrewRole, string>> = { gunner: 'turret', engineer: 'engineer' };
/** The engineer's station, hull-local metres (aft of the turret, inside the spine). */
export const ENGINEER_SEAT: readonly [number, number, number] = [0, 2.2, 4.0];
export const INVITE_TTL_S = 120;
export const LEASE_TTL_S = 3 * 3600;
/** A gunner / engineer whose connection drops keeps the station this long, then it is released. */
export const DISCONNECT_GRACE_S = 30;
export const KIT_BUDGET_MAX = 5;
export const CREW_ROLE_LABEL: Readonly<Record<CrewRole, string>> = { gunner: 'gunner', engineer: 'engineer' };
export function epochKey(shipId: bigint | number | string, role: CrewRole): string { return `${String(shipId)}:${role}`; }
export interface LeaseFacts {
  state: string; endReason: string; holderIsSender: boolean; role: string; epoch: number; actions: string;
  expiresAtMs: number; shipAlive: boolean; captainFlies: boolean;
}
/** Is a crew command (carrying `epoch`) still authorised? Ordered refusals; `epochNow` is the hull-role's counter. */
export function leaseCommand(l: LeaseFacts | null, epochNow: number, epoch: number, action: string, nowMs: number): { ok: true } | { ok: false; why: string } {
  if (!l || !l.holderIsSender) return { ok: false, why: 'You hold no crew lease on that hull' };
  if (l.state !== 'active') return { ok: false, why: `Your ${l.role} lease ended (${l.endReason || l.state}) — that order is void` };
  if (!l.shipAlive) return { ok: false, why: 'That hull is gone — a lost hull is never worked on' };
  if (!l.captainFlies) return { ok: false, why: 'The captain no longer flies that hull — the lease ends with the swap' };
  if (epoch !== l.epoch || l.epoch !== epochNow) return { ok: false, why: `That order carries epoch ${epoch}; the ${l.role} station is at epoch ${epochNow} — it was handed over, so the order is void` };
  if (nowMs > l.expiresAtMs) return { ok: false, why: 'Your lease expired — ask the captain to invite you again' };
  if (!l.actions.split(',').includes(action)) return { ok: false, why: `The captain did not grant "${action}" to the ${l.role}` };
  return { ok: true };
}

// ── THE FIT REVISION: a fingerprint of the hull's fitted components (a fit change voids a reserved patch) ───────────
export function fitRevision(rows: ReadonlyArray<{ slot: string; itemKey: string; quality: number; serial: string }>): string {
  const parts = rows.map((r) => `${r.slot}|${r.itemKey}|${r.quality}|${r.serial}`).sort();
  let h = 0x811c9dc5, g = 0x01000193 ^ 0x5bd1e995;
  for (const p of parts) for (let i = 0; i < p.length; i++) { h ^= p.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; g ^= p.charCodeAt(i); g = Math.imul(g, 0x5bd1e995) >>> 0; }
  return `${rows.length}:${(h >>> 0).toString(16)}${(g >>> 0).toString(16)}`;
}

// ── THE DROID'S FIELD STOCK: passive nursing and the emergency patch draw on a finite stock restocked at a safe dock ─
export const DROID_STOCK_BASE = 28;
export const DROID_STOCK_PER = 48;
export const DROID_NURSE_RATE = 0.8;   // hp/s × droid (the shipped rate)
export const DROID_PATCH_BASE = 12;    // V: 12 + 18·droid (the shipped patch)
export const DROID_PATCH_PER = 18;
export function droidFieldStock(droid: number): number {
  return droid > 0 && Number.isFinite(droid) ? Math.round(DROID_STOCK_BASE + DROID_STOCK_PER * droid) : 0;
}

// ── THE HUD'S WORDS (§8.2, §22.2): cause and action, a glyph and text — never colour alone ───────────────────────────
export interface GroupLine { group: SubsystemGroup; state: GroupState; glyph: string; text: string }
/** `keys` are the CURRENT bindings, printed by the caller's key helper (THE ONE-KEY LAW). */
export function groupLine(group: SubsystemGroup, health: number, keys: { reroute: string; patch: string }): GroupLine {
  const h = clampHealth(health), s = groupState(h);
  if (s === 'healthy') return { group, state: s, glyph: '✓', text: `${group === 'engine' ? 'Engine' : 'Weapons'} ${h}` };
  if (group === 'engine') {
    return s === 'degraded'
      ? { group, state: s, glyph: '⚠', text: `Engine degraded (${h}) — acceleration reduced — reroute (${keys.reroute}) or request repair (${keys.patch})` }
      : { group, state: s, glyph: '✖', text: `Engine DISABLED (${h}) — limping at ${Math.round(ENGINE_LIMP * 100)} % thrust, steering holds — patch it (${keys.patch}) or request repair` };
  }
  return s === 'degraded'
    ? { group, state: s, glyph: '⚠', text: `Weapons degraded (${h}) — cannons cycle ${Math.round((WEAPONS_DEGRADED_CD - 1) * 100)} % slower — reroute (${keys.reroute}) or request repair (${keys.patch})` }
    : { group, state: s, glyph: '✖', text: `Cannons DISABLED (${h}) — they cannot fire; the missile rack still can — patch them (${keys.patch}) or request repair` };
}
/** The worst group (the cockpit shortcut patches it); null when both are at or above the field cap. */
export function worstPatchable(health: Readonly<GroupHealth>): SubsystemGroup | null {
  let best: SubsystemGroup | null = null;
  for (const g of SUBSYSTEM_GROUPS) {
    const h = clampHealth(health[g]);
    if (h >= FIELD_CAP) continue;
    if (best === null || h < clampHealth(health[best])) best = g;
  }
  return best;
}
